import { createContext, useContext, useEffect, useCallback, useState } from 'react';
import Intercom from '@intercom/messenger-js-sdk';
import { useAppConfig } from '../../contexts/AppConfigContext';

interface IntercomContextType {
  /** Open the Intercom messenger window (e.g. from the sidebar "Help" button). */
  openIntercom: () => void;
  /** Open the messenger on a NEW conversation with the composer prefilled (e.g. a Routy escalation snapshot). */
  openIntercomWithMessage: (message: string) => void;
  /** Close the Intercom messenger window. */
  hideIntercom: () => void;
  /** @deprecated Prefer `openIntercom`. Kept for older callers. */
  showIntercom: () => void;
  /** True while the Intercom messenger window is open (via onShow/onHide). */
  isIntercomOpen: boolean;
}

const IntercomContext = createContext<IntercomContextType | undefined>(undefined);

/**
 * Hook to control Intercom. The default floating launcher is hidden app-wide
 * (see IntercomProvider) so it doesn't collide with the Routy launcher in the
 * bottom-right corner — instead, open the messenger from the sidebar "Help"
 * button (desktop) or the "Support" button in the mobile menu.
 */
export const useIntercom = () => {
  const context = useContext(IntercomContext);
  if (!context) {
    // Return no-op functions if used outside provider (e.g., in public booking flow)
    return {
      openIntercom: () => {},
      openIntercomWithMessage: () => {},
      hideIntercom: () => {},
      showIntercom: () => {},
      isIntercomOpen: false,
    };
  }
  return context;
};

/**
 * IntercomProvider — initializes Intercom messenger for authenticated users.
 *
 * Reads identity straight from <c>useAppConfig().config</c> — already loaded
 * once on app boot via the app-configuration endpoint. No extra
 * API calls. <c>userCreationTime</c> arrives under
 * <c>config.extraProperties.userCreationTime</c>, injected server-side by
 * <c>CurrentUserCreationTimeContributor</c>.
 *
 * The default floating launcher bubble is hidden (`hide_default_launcher`)
 * so the bottom-right corner belongs to the Routy launcher alone. Users open
 * Intercom from the sidebar "Help" button (desktop) or the mobile menu
 * "Support" button — both call `Intercom('show')`.
 */
export const IntercomProvider = ({ children }: { children: React.ReactNode }) => {
  const { config } = useAppConfig();
  const currentUser = config?.currentUser;
  const [isIntercomOpen, setIsIntercomOpen] = useState(false);

  useEffect(() => {
    if (!currentUser?.isAuthenticated || !currentUser.id) {
      if (typeof window !== 'undefined' && window.Intercom) {
        window.Intercom('shutdown');
      }
      return;
    }

    const fullName = [currentUser.name, currentUser.surName]
      .filter(Boolean)
      .join(' ')
      .trim();

    // Server emits userCreationTime as an ISO-8601 string under the typed
    // extraProperties dictionary (Record<string, any>) on the config DTO.
    const creationTimeRaw = config?.extraProperties?.userCreationTime as string | undefined;
    const createdAtSeconds = creationTimeRaw
      ? Math.floor(new Date(creationTimeRaw).getTime() / 1000)
      : undefined;

    Intercom({
      app_id: 'it9kfods',
      user_id: currentUser.id,
      name: fullName || currentUser.userName || undefined,
      email: currentUser.email || undefined,
      created_at: createdAtSeconds,
      // Never show Intercom's own floating launcher bubble — it lives in the
      // bottom-right corner where the Routy launcher now sits. Support is
      // reached through the sidebar "Help" button (desktop) / mobile menu
      // "Support" button instead, both of which call `Intercom('show')`.
      // Because the bubble is permanently hidden, there's no longer any need
      // to toggle it around modals (the previous MutationObserver did that).
      hide_default_launcher: true,
    });

    // Track messenger open/close so global UI (e.g. the Routy launcher) can
    // get out of the way while the support window is open. These handlers
    // replace any previously-registered one, so re-running on re-boot is safe.
    // `onShow`/`onHide` take a callback rather than the options object the
    // window.Intercom type declares, so route through a locally-typed ref.
    if (typeof window !== 'undefined' && window.Intercom) {
      const intercom = window.Intercom as (command: string, cb: () => void) => void;
      intercom('onShow', () => setIsIntercomOpen(true));
      intercom('onHide', () => setIsIntercomOpen(false));
    }
  }, [currentUser, config]);

  // Open the messenger window (launcher bubble stays hidden regardless).
  const openIntercom = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('show');
    }
  }, []);

  // Open a NEW conversation with the composer prefilled — used by Routy's
  // support card to hand the team a context snapshot of what went wrong.
  const openIntercomWithMessage = useCallback((message: string) => {
    if (typeof window !== 'undefined' && window.Intercom) {
      const intercom = window.Intercom as (command: string, arg?: unknown) => void;
      intercom('showNewMessage', message);
    }
  }, []);

  const hideIntercom = useCallback(() => {
    if (typeof window !== 'undefined' && window.Intercom) {
      window.Intercom('hide');
    }
  }, []);

  return (
    <IntercomContext.Provider value={{ openIntercom, openIntercomWithMessage, hideIntercom, showIntercom: openIntercom, isIntercomOpen }}>
      {children}
    </IntercomContext.Provider>
  );
};

// Add type declaration for window.Intercom
declare global {
  interface Window {
    Intercom: (command: string, options?: Record<string, unknown>) => void;
  }
}

export default IntercomProvider;
